import React from 'react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { classes, st } from './IntakeFormSection.st.css';
import { IntakeFormSectionDataHook } from './dataHooks';
import {
  Text,
  IconButton,
  ButtonSize,
  ButtonPriority,
} from 'wix-ui-tpa/cssVars';
import ChevronRight from 'wix-ui-icons-common/on-stage/ChevronRight';
import SelectFill from 'wix-ui-icons-common/on-stage/SelectFill';

import { useFormActions } from '../../Hooks/useFormActions';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  IntakeForms,
  IntakeForm,
  IntakeFormStatus,
} from '../../../../utils/state/types';
import { mapToArray } from '../../../../utils';
import { getErrorByType } from '../../../../utils/errors/errors';
import { IntakeFormErrorType, FormError } from '../../../../types/errors';
import { IntakeFormStatusBadge } from './IntakeFormStatusBadge/IntakeFormStatusBadge';
import {
  SectionMessage,
  SectionMessageType,
} from '../SectionMessage/SectionMessage';

const IntakeFormSection: React.FC<{
  intakeForms?: IntakeForms;
  errors?: FormError[];
}> = ({ intakeForms, errors = [] }) => {
  const { isMobile } = useEnvironment();
  const { openIntakeFormLightbox, removeErrors } = useFormActions();
  const { wixFormValues } = useFormComponentContext();
  const { experiments } = useExperiments();
  const { t } = useTranslation();

  const isUOUIntakeFormsIntegrationEnabled = experiments.enabled(
    ExperimentsConsts.UOUIntakeFormsIntegration,
  );

  if (!isUOUIntakeFormsIntegrationEnabled || !intakeForms) {
    return null;
  }

  const intakeFormsArray = mapToArray<IntakeForm>(intakeForms);

  if (intakeFormsArray.length === 0) {
    return null;
  }

  const intakeFormError = getErrorByType({
    errors,
    errorType: IntakeFormErrorType,
  });

  const getErrorText = (errorType: IntakeFormErrorType) => {
    return (
      intakeFormError.params?.message ||
      t(`app.intake-form-section.error.${errorType.toLowerCase()}`)
    );
  };

  const handleClick = (formId: string) => {
    openIntakeFormLightbox(formId, wixFormValues);
    removeErrors([IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED]);
  };

  return (
    <div
      className={st(classes.root, { isMobile })}
      data-hook={IntakeFormSectionDataHook.ROOT}
    >
      <div className={classes.sectionHeader}>
        <Text
          className={classes.sectionTitle}
          data-hook={IntakeFormSectionDataHook.SECTION_TITLE}
        >
          {t('app.intake-form-section.title.default')}
        </Text>
        <Text
          className={classes.subtitle}
          data-hook={IntakeFormSectionDataHook.SECTION_SUBTITLE}
        >
          {t('app.intake-form-section.subtitle.default')}
        </Text>
      </div>

      {intakeFormsArray.map((form) => (
        <div
          key={form.formId}
          className={classes.formCard}
          data-hook={IntakeFormSectionDataHook.FORM_CARD}
          data-form-id={form.formId}
          onClick={() => handleClick(form.formId!)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            e.preventDefault();
            handleClick(form.formId!);
          }}
        >
          <div
            className={classes.container}
            data-hook={IntakeFormSectionDataHook.SECTION_CONTAINER}
          >
            <div className={classes.titleContainer}>
              {form.status === IntakeFormStatus.COMPLETED && (
                <SelectFill className={classes.completedIcon} aria-hidden />
              )}
              <Text
                className={classes.formTitle}
                data-hook={IntakeFormSectionDataHook.FORM_TITLE}
              >
                {/* hardcoded for now!! */}
                Waiver Name
              </Text>
              <IntakeFormStatusBadge status={form.status} />
            </div>
            <IconButton
              icon={<ChevronRight aria-hidden />}
              size={ButtonSize.small}
              priority={ButtonPriority.primary}
              data-hook={IntakeFormSectionDataHook.CHEVRON_BUTTON}
              aria-label="collapse intake form"
            />
          </div>
        </div>
      ))}
      {intakeFormError && (
        <SectionMessage
          type={SectionMessageType.Error}
          text={getErrorText(intakeFormError.errorType as IntakeFormErrorType)}
          className={classes.errorNotification}
        />
      )}
    </div>
  );
};

export default IntakeFormSection;
